import React, { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, mutate } from '../../shared/api';
import { Btn, Card, DataTable, Pill, Select, type Col } from '../../shared/ui';
import {
    ATTR_LABEL, Field, FormGrid, Input, Note, SETTLEMENT_LABEL, Section, day, eur, num, tone,
    type Attribution, type Intermediary, type Props, type Settlement,
} from './posrShared';

/** m.1.2 Pripis kupnji, umanjenja i mjesecni obracun provizije. */

export function PosrAttributions({ token, onError }: Props) {
    const [rows, setRows] = useState<Attribution[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [ret, setRet] = useState<Attribution | null>(null);
    const [qty, setQty] = useState('');
    const [date, setDate] = useState('');
    const [ref, setRef] = useState('');

    const load = useCallback(() => {
        setRows(null);
        api<{ rows: Attribution[] }>('/posrednici/attributions', token).then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, onError]);
    useEffect(load, [load]);

    const sync = async () => {
        setBusy(true);
        try {
            const r = await mutate<{ leads: number; upserted: number; storno: number }>('/posrednici/sync', token, 'POST');
            onError(`Pripis je osvježen za ${r.leads} prihvaćenih prijava, upisano ili ažurirano ${r.upserted} stavki, a u storno je otišlo ${r.storno}.`);
            load();
        } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const saveReturn = async () => {
        if (!ret) return;
        setBusy(true);
        try {
            const r = await mutate<{ amount_eur: number }>('/posrednici/adjustments', token, 'POST', {
                type: 'povrat_zlata', attribution_id: ret.id, quantity: Number(qty.replace(',', '.')), happened_at: date, reference: ref || null,
            });
            onError(`Umanjenje od ${eur(r.amount_eur)} ide u sljedeći obračun.`);
            setRet(null); setQty(''); setDate(''); setRef('');
        } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const cols: Col<Attribution>[] = [
        { key: 'datum_prihvacanja', label: 'Prihvaćeno', render: (r) => day(r.datum_prihvacanja) },
        { key: 'intermediary_name', label: 'Posrednik' },
        { key: 'client_name', label: 'Klijent', render: (r) => `${r.client_name} (${r.customer_code})` },
        { key: 'broj_ponude', label: 'Ponuda' },
        { key: 'naziv_artikla', label: 'Artikl', render: (r) => r.naziv_artikla ?? '-' },
        { key: 'kolicina', label: 'Kol.', align: 'right', render: (r) => num(r.kolicina) },
        { key: 'iznos_sa_pdv', label: 'Iznos', align: 'right', render: (r) => eur(r.iznos_sa_pdv) },
        { key: 'ruc_eur', label: 'RUC', align: 'right', render: (r) => eur(r.ruc_eur) },
        { key: 'list_commission_eur', label: 'Po cjeniku', align: 'right', render: (r) => eur(r.list_commission_eur) },
        { key: 'cap_eur', label: 'Granica 25 %', align: 'right', render: (r) => eur(r.cap_eur) },
        { key: 'commission_eur', label: 'Provizija', align: 'right', render: (r) => <strong>{eur(r.commission_eur)}</strong> },
        { key: 'vat_rate', label: 'PDV', align: 'right', render: (r) => r.sale_vat_rate === null ? '?' : `${num(r.vat_rate, 0)} %` },
        { key: 'vat_eur', label: 'PDV iznos', align: 'right', render: (r) => eur(r.vat_eur) },
        { key: 'status', label: 'Status', render: (r) => <Pill tone={tone(r.status)}>{ATTR_LABEL[r.status] ?? r.status}</Pill> },
        { key: 'act', label: '', render: (r) => r.status === 'obracunato' ? <Btn size="sm" onClick={() => setRet(r)}>Povrat zlata</Btn> : null },
    ];

    return (
        <Card>
            <Section title="Pripis kupnji kanalu Posrednik" actions={<Btn variant="primary" disabled={busy} onClick={sync}>Osvježi pripis</Btn>}>
                <Note>
                    Kupnja se pripisuje sama kad je ponuda kupca s prihvaćenom prijavom realizirana računom, prva kupnja padne u 90 dana od prijave, a kupnja u 12 mjeseci od prve kupnje.
                    Provizija je manji od dva iznosa, po cjeniku ili 25 posto stvarnog RUC-a stavke, i iskazana je bez PDV-a.
                </Note>
                {ret ? (
                    <Card pad={14}>
                        <FormGrid>
                            <Field label={`Vraćena količina (od ${num(ret.kolicina)})`}><Input value={qty} onChange={setQty} /></Field>
                            <Field label="Datum otkupa"><Input type="date" value={date} onChange={setDate} /></Field>
                            <Field label="Dokument otkupa"><Input value={ref} onChange={setRef} /></Field>
                        </FormGrid>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <Btn onClick={() => setRet(null)}>Odustani</Btn>
                            <Btn variant="primary" disabled={busy || !qty || !date} onClick={saveReturn}>Upiši umanjenje</Btn>
                        </div>
                    </Card>
                ) : null}
                <DataTable cols={cols} rows={rows} empty="Nema pripisanih kupnji." />
            </Section>
        </Card>
    );
}

export function PosrSettlements({ token, isAdmin, onError }: Props) {
    const [rows, setRows] = useState<Settlement[] | null>(null);
    const [intermediaries, setIntermediaries] = useState<Intermediary[]>([]);
    const [iid, setIid] = useState('');
    const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
    const [pay, setPay] = useState<Settlement | null>(null);
    const [invoice, setInvoice] = useState('');
    const [paidAt, setPaidAt] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setRows(null);
        api<{ rows: Settlement[] }>('/posrednici/settlements', token).then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, onError]);
    useEffect(load, [load]);
    useEffect(() => {
        api<{ rows: Intermediary[] }>('/posrednici/intermediaries', token).then((d) => setIntermediaries(d.rows)).catch(() => undefined);
    }, [token]);

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true);
        try { await fn(); load(); } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const cols: Col<Settlement>[] = [
        { key: 'period', label: 'Mjesec' },
        { key: 'intermediary_name', label: 'Posrednik' },
        { key: 'commission_eur', label: 'Provizija', align: 'right', render: (r) => eur(r.commission_eur) },
        { key: 'deductions_eur', label: 'Umanjenja', align: 'right', render: (r) => eur(r.deductions_eur) },
        { key: 'carried_in_eur', label: 'Preneseno', align: 'right', render: (r) => eur(r.carried_in_eur) },
        { key: 'payable_eur', label: 'Za isplatu', align: 'right', render: (r) => <strong>{eur(r.payable_eur)}</strong> },
        { key: 'payable_vat_eur', label: 'PDV', align: 'right', render: (r) => eur(r.payable_vat_eur) },
        { key: 'payable_gross', label: 'S PDV-om', align: 'right', render: (r) => eur(Number(r.payable_eur) + Number(r.payable_vat_eur)) },
        { key: 'carried_out_eur', label: 'Prenosi se', align: 'right', render: (r) => eur(r.carried_out_eur) },
        { key: 'status', label: 'Status', render: (r) => <Pill tone={tone(r.status)}>{SETTLEMENT_LABEL[r.status] ?? r.status}</Pill> },
        { key: 'invoice_number', label: 'Račun', render: (r) => r.invoice_number ? `${r.invoice_number}, ${day(r.paid_at)}` : '-' },
        {
            key: 'act', label: '', render: (r) => (
                <div style={{ display: 'flex', gap: 6 }}>
                    <Btn size="sm" onClick={() => downloadFile(`/posrednici/settlements/${r.id}/export`, token, `obracun-${r.period}.xlsx`).catch((e: Error) => onError(e.message))}>Tablica</Btn>
                    {r.status === 'nacrt' ? <Btn size="sm" disabled={busy} onClick={() => run(() => mutate('/posrednici/settlements', token, 'POST', { intermediary_id: r.intermediary_id, period: r.period }))}>Preračunaj</Btn> : null}
                    {isAdmin && r.status === 'nacrt' ? <Btn size="sm" variant="soft" disabled={busy} onClick={() => run(() => mutate(`/posrednici/settlements/${r.id}/close`, token, 'POST'))}>Zaključi</Btn> : null}
                    {isAdmin && r.status === 'zakljucen' ? <Btn size="sm" variant="soft" onClick={() => setPay(r)}>Isplata</Btn> : null}
                </div>
            ),
        },
    ];

    return (
        <Card>
            <Section title="Mjesečni obračun" actions={<>
                <Select value={iid} onChange={setIid}>
                    <option value="">Posrednik</option>
                    {intermediaries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </Select>
                <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ height: 38, borderRadius: 10, border: '1px solid #e7e9f0', padding: '0 10px' }} />
                <Btn variant="primary" disabled={busy || !iid} onClick={() => run(() => mutate('/posrednici/settlements', token, 'POST', { intermediary_id: Number(iid), period }))}>Izračunaj</Btn>
            </>}>
                <Note>Obračun skuplja sve neobračunate kupnje i umanjenja do kraja mjeseca. Iznos ispod 50 eura, ili negativan saldo, prenosi se u idući mjesec. Zaključen obračun se više ne računa.</Note>
                {pay ? (
                    <Card pad={14}>
                        <FormGrid>
                            <Field label="Broj ulaznog računa posrednika"><Input value={invoice} onChange={setInvoice} /></Field>
                            <Field label="Datum isplate"><Input type="date" value={paidAt} onChange={setPaidAt} /></Field>
                        </FormGrid>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <Btn onClick={() => setPay(null)}>Odustani</Btn>
                            <Btn variant="primary" disabled={busy || !invoice || !paidAt}
                                onClick={() => run(async () => { await mutate(`/posrednici/settlements/${pay.id}/pay`, token, 'POST', { invoice_number: invoice, paid_at: paidAt }); setPay(null); })}>Upiši isplatu</Btn>
                        </div>
                    </Card>
                ) : null}
                <DataTable cols={cols} rows={rows} empty="Nema obračuna." />
            </Section>
        </Card>
    );
}
